// Guest flow, step 2 of 3: 6-digit code entry against the SAME email
// captured in step 1 (never re-editable here — "change email" restarts
// verification from step 1 entirely, matching the backend's own
// destination+purpose keying — backend/src/services/guestService.js).

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import ErrorState from './ErrorState.jsx';
import styles from './GuestVerifyStep.module.css';

// idPrefix — same reuse mechanism as GuestEmailStep.jsx's own prop; see that
// file's header comment. Defaults to 'guestVerify' so GuestPage.jsx is
// unaffected.
export default function GuestVerifyStep({
  email,
  onSubmit,
  isSubmitting,
  submitErrorId,
  submitError,
  onResend,
  isResending,
  resendErrorId,
  resendError,
  resendNoticeShown,
  onChangeEmail,
  idPrefix = 'guestVerify',
}) {
  const { t } = useLanguage();
  const [code, setCode] = useState('');
  const [touched, setTouched] = useState(false);

  const isValid = /^\d{6}$/.test(code);

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!isValid) return;
    onSubmit(code);
  };

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <h1 className={styles.title}>{t(`portal.${idPrefix}.title`)}</h1>
      <p className={styles.subtitle}>{t(`portal.${idPrefix}.subtitle`, { email })}</p>

      {submitErrorId && <ErrorState messageId={submitErrorId} error={submitError} />}
      {resendNoticeShown && <p className={styles.resentNotice}>{t(`portal.${idPrefix}.resent`)}</p>}

      <label className={styles.field}>
        <span className={styles.label}>{t(`portal.${idPrefix}.codeLabel`)}</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          className={`${styles.input} ${styles.codeInput} tabularNums`}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        />
        {touched && !isValid && (
          <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
        )}
      </label>

      <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
        {isSubmitting ? t(`portal.${idPrefix}.submitting`) : t(`portal.${idPrefix}.submit`)}
      </button>

      <div className={styles.linksRow}>
        <button
          type="button"
          className={styles.linkButton}
          onClick={onResend}
          disabled={isResending}
        >
          {isResending ? t(`portal.${idPrefix}.resending`) : t(`portal.${idPrefix}.resend`)}
        </button>
        <button type="button" className={styles.linkButton} onClick={onChangeEmail}>
          {t(`portal.${idPrefix}.changeEmail`)}
        </button>
      </div>
      {resendErrorId && <ErrorState messageId={resendErrorId} error={resendError} />}
    </form>
  );
}
